import { createCustomerPortalSession } from "@/api/privateRequests/stripe/createCustomerPortalSession";
import Button from "@/components/Button";
import { cn } from "@/lib/utils";
import { Subscription } from "@/redux/user/userSlice";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { navigationProfileIcon } from "../NavBarTouchDevice/components/NavIcons";
import { NavDropdown } from "./components/NavDropdown";
import { NavLinkDesktop } from "./components/NavLinkDesktop";

export interface NavItem {
  label: string;
  to: string;
  isPaywall?: boolean;
  canUseClubImport?: boolean;
  isEliteUser?: boolean;
}

export interface ExternalNavItem {
  imgUrl?: string;
  externalLink: true;
  to: string;
  label?: string;
}

export const NavBarDesktop = ({
  currentRoute,
  userIsLoggedIn,
  userSubscription,
  freeUser,
  paidUserButNotUltimate,
  handleClick,
}: {
  currentRoute: string;
  userIsLoggedIn: boolean;
  userSubscription: Subscription;
  freeUser: boolean;
  paidUserButNotUltimate: boolean;
  handleClick: ({ isPaywalled }: { isPaywalled: boolean }) => void;
}) => {
  const navigate = useNavigate();
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setHasScrolled(scrollTop > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems: NavItem[] = [
    { label: "SBCs", to: "/" },
    { label: "Squad Builder", to: "/squad-builder" },
    { label: "Players", to: "/players" },
    { label: "Evolutions", to: "/evolutions" },
    { label: "Tactics", to: "/tactics" },
    { label: "Meta Rating", to: "/meta-rating" },
    {
      label: "My Club",
      to: "/my-club",
      isPaywall: true,
    },
  ];
  const dropdownItems: (NavItem | ExternalNavItem)[] = [
    { label: "Subscription", to: "/subscription" },
    { label: "News", to: "/news" },
    { label: "Careers", to: "https://careers.easysbc.io/", externalLink: true },
    { label: "Contact", to: "/contact" },
    { label: "Who Are We?", to: "/who-are-we" },
    {
      imgUrl:
        import.meta.env.VITE_APP_ASSETS_BASE_URL + "/fut23/misc/discord-v2.svg",
      to: "https://discord.gg/mmNtdnUcJf",
      label: "Discord",
      externalLink: true,
    },
    {
      imgUrl:
        import.meta.env.VITE_APP_ASSETS_BASE_URL + "/fut23/misc/twitter.svg",
      to: "https://x.com/easysbc",
      label: "Twitter",
      externalLink: true,
    },
    {
      label: "Instagram",
      imgUrl:
        import.meta.env.VITE_APP_ASSETS_BASE_URL + "/fut23/misc/instagram.svg",
      to: "https://www.instagram.com/easysbc/",
      externalLink: true,
    },
  ];

  return (
    <nav
      className={cn(
        `mx-auto bg-new-gray-950 w-full h-12 flex items-center px-4 large-desktop:px-8`,
        hasScrolled && "shadow-navigation-bar",
      )}
    >
      <div className="flex items-center justify-between mx-auto w-full max-w-content">
        <div className="flex items-center large-desktop:gap-x-3">
          <div className="large-desktop:mr-3">
            <NavLinkDesktop to="/" currentRoute={currentRoute} disableHover>
              <img
                src={import.meta.env.VITE_APP_ASSETS_BASE_URL + "/easysbc1.png"}
                alt="EasySBC"
                className="h-7"
              />
            </NavLinkDesktop>
          </div>
          {navItems.map((item) => (
            <NavLinkDesktop
              key={item.to}
              to={item.to}
              currentRoute={currentRoute}
              onClick={() => handleClick({ isPaywalled: item.isPaywall })}
            >
              {item.label}
            </NavLinkDesktop>
          ))}
          <NavDropdown
            navDropdownItems={dropdownItems}
            currentRoute={currentRoute}
          />
        </div>
        <div className="flex flex-row items-center gap-x-3">
          <NavLinkDesktop
            to={userIsLoggedIn ? "/profile" : "/login"}
            currentRoute={currentRoute}
          >
            {userIsLoggedIn ? (
              <div className="flex flex-row gap-x-2 items-center">
                <div className="w-5 h-5">{navigationProfileIcon}</div>
                Profile
              </div>
            ) : (
              "Log in"
            )}
          </NavLinkDesktop>
          {userSubscription !== "diamond" && (
            <Button
              variant="primary"
              label={
                freeUser
                  ? "Upgrade"
                  : paidUserButNotUltimate
                    ? "Get Ultimate"
                    : ""
              }
              onClick={() => {
                if (paidUserButNotUltimate) {
                  createCustomerPortalSession();
                } else {
                  navigate("/subscription");
                }
              }}
            />
          )}
        </div>
      </div>
    </nav>
  );
};
